// Drives the "auto Client login" UX for Cognito-enabled builds.
//
// Desired behavior (Cognito only):
//   - On ANY unauthenticated route, immediately kick off the non-ZS Client
//     flow (`signInWithRedirect()` via the NON_ZS_LOGIN_PROVIDER IdP) instead
//     of showing the in-app picker first.
//   - If there's already a valid Cognito session, do nothing — the app renders
//     normally ("opens flawlessly").
//   - If Cognito can't authenticate (Hosted UI misconfigured, redirect failure,
//     repeated bounce-back without a session), fall back to the in-app /login
//     picker so the user is never stuck on a blank screen.
//
// `signInWithRedirect()` navigates the whole tab to the Hosted UI, so we cannot
// "catch" an unauthenticated user inline — we must check for an existing session
// first and only redirect when there isn't one. A sessionStorage flag prevents
// an infinite redirect loop when the IdP keeps returning without a session.

import { signInWithRedirect } from "aws-amplify/auth";
import { isCognitoLoginAllowed } from "src/config/loginMethods";
import { getCognitoToken, clearCognitoToken } from "./cognitoAuth";
import { environment } from "src/config/environment";

// Set just before we navigate to the Hosted UI; checked on the bounce-back so a
// still-unauthenticated return resolves to the picker instead of redirecting again.
const AUTO_ATTEMPT_KEY = "cognito_auto_login_attempted";

// Set by the logout handler so the /login page can show a "signed out" message.
// Consumed (read + removed) on /login mount. Uses localStorage (not
// sessionStorage) because the Cognito Hosted-UI /logout round-trip is
// cross-origin and sessionStorage does not survive it.
const SIGNED_OUT_KEY = "cognito_signed_out";

// Stashes the provider the user picked when we have to bounce through a
// Hosted-UI sign-out first (stale session). Read back on return to /login to
// auto-resume that exact provider's login. sessionStorage survives the
// cross-origin round-trip to Cognito and back within the same tab.
const PENDING_PROVIDER_KEY = "cognito_pending_login_provider";

// Cognito federated-IdP names (the `custom:` provider handed to
// signInWithRedirect), sourced from env so the IdP wired into the User Pool can
// change per tenant without a code change. Defaults match the current testing
// setup (ZS-Internal for ZS employees, ZS-Entra-Dev for non-ZS clients).
//
// The default (non-ZS / "Client") login provider, used by the auto-login flow
// and the non-ZS button in src/pages/Login/index.tsx.
export const NON_ZS_LOGIN_PROVIDER = environment.REACT_APP_COGNITO_CLIENT_PROVIDER;

// The ZS-employee login provider, used by the ZS button in Login/index.tsx.
export const ZS_LOGIN_PROVIDER = environment.REACT_APP_COGNITO_ZS_PROVIDER;

export type AutoLoginOutcome =
	| "authenticated" // valid session — render the app
	| "redirecting" // navigating to Hosted UI (tab will unload)
	| "show-picker"; // auth unavailable/failed — render the /login picker fallback

const buildLoginPath = (): string => {
	const basePath = (environment.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
	return `${basePath}/login`;
};

const isOnLoginRoute = (): boolean => typeof window !== "undefined" && window.location.pathname.endsWith("/login");

// Hosted UI reachability probe.
//
// `signInWithRedirect()` does a full-page navigation to the Cognito Hosted UI.
// If that Hosted UI is broken (domain not provisioned, app client missing the
// callback URL, IdP disabled) Cognito renders a terminal "Login pages
// unavailable" page on its OWN domain. At that point our JS is gone and we can
// never bounce the user back. So we must decide BEFORE navigating away.
//
// We probe the Hosted UI domain with a lightweight no-cors request. A working
// Hosted UI responds (opaque but resolved); a non-existent / unprovisioned
// domain fails DNS/TLS/connection and rejects. This can't read the response
// body (CORS), but a hard network failure is exactly the "dead-end" signal we
// need — enough to keep us in-app and show our own /login picker instead.
const HOSTED_UI_PROBE_TIMEOUT_MS = 4000;

const isHostedUiReachable = async (): Promise<boolean> => {
	const domain = environment.REACT_APP_COGNITO_DOMAIN;
	if (!domain) return false;

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), HOSTED_UI_PROBE_TIMEOUT_MS);
	try {
		// no-cors: we only care whether the host is reachable, not the body.
		await fetch(`https://${domain}/`, {
			method: "HEAD",
			mode: "no-cors",
			signal: controller.signal,
			cache: "no-store",
		});
		return true;
	} catch {
		// DNS failure, TLS failure, connection refused, or timeout — treat the
		// Hosted UI as unavailable and stay in-app.
		return false;
	} finally {
		clearTimeout(timer);
	}
};

// True when Cognito has bounced us back to our redirect URI with an OAuth
// error (e.g. ?error=access_denied or ?error=invalid_request). This is the
// "login failed but the IdP DID return to us" case — distinct from a silent
// unauthenticated bounce-back. We treat it as a hard signal to show the picker
// instead of attempting another redirect.
const hasOAuthErrorParam = (): boolean => {
	if (typeof window === "undefined") return false;
	try {
		const params = new URLSearchParams(window.location.search);
		return params.has("error") || params.has("error_description");
	} catch {
		return false;
	}
};

/**
 * Resolve what should happen for a Cognito build on an unauthenticated entry.
 * Safe to call on every route: it short-circuits to "authenticated" when a
 * session exists and is idempotent against the loop-guard flag.
 */
export const resolveCognitoAutoLogin = async (): Promise<AutoLoginOutcome> => {
	if (!isCognitoLoginAllowed) return "authenticated";

	// Cognito returned to us with an explicit OAuth error (login failed at the
	// IdP). Don't retry the redirect — surface our own /login picker so the user
	// isn't ping-ponged back into the failing flow. Keep the attempt guard set so
	// the picker (not another auto-redirect) is what they see.
	if (hasOAuthErrorParam()) {
		return "show-picker";
	}

	const token = await getCognitoToken();
	if (token) {
		// Clean session — clear the loop guard so a future logout→login works.
		try {
			sessionStorage.removeItem(AUTO_ATTEMPT_KEY);
		} catch {
			// sessionStorage unavailable (private mode / SSR) — non-fatal.
		}
		return "authenticated";
	}

	// No session. If we already bounced the user to the Hosted UI once and they
	// came back still unauthenticated, stop redirecting and show the picker.
	let alreadyAttempted = false;
	try {
		alreadyAttempted = sessionStorage.getItem(AUTO_ATTEMPT_KEY) === "true";
	} catch {
		alreadyAttempted = false;
	}

	if (alreadyAttempted) {
		return "show-picker";
	}

	// First attempt. Before burning it on a full-page navigation we can't come
	// back from, confirm the Hosted UI is actually reachable. If the domain is
	// unprovisioned/broken, signInWithRedirect would strand the user on Cognito's
	// own "Login pages unavailable" page with no JS left to bounce them back — so
	// fall through to our own /login picker instead. Probe BEFORE setting the
	// guard so a transient probe failure doesn't permanently burn the attempt.
	if (!(await isHostedUiReachable())) {
		return "show-picker";
	}

	// Mark the attempt, then redirect to the appropriate Hosted UI.
	try {
		sessionStorage.setItem(AUTO_ATTEMPT_KEY, "true");
	} catch {
		// If we can't persist the guard, fall back to the picker rather than risk
		// an infinite redirect loop.
		return "show-picker";
	}

	const provider = NON_ZS_LOGIN_PROVIDER;
	console.log(`[cognito-auto] redirecting to client provider: ${provider}`);

	try {
		await signInWithRedirect({ provider: { custom: provider } });
		return "redirecting";
	} catch (error) {
		console.error("Cognito auto login failed:", error);
		return "show-picker";
	}
};

/** Clears the one-shot auto-login guard. Call after an explicit sign-out. */
export const resetCognitoAutoLoginGuard = (): void => {
	try {
		sessionStorage.removeItem(AUTO_ATTEMPT_KEY);
	} catch {
		// non-fatal
	}
};

/**
 * Arm the auto-login guard so the NEXT unauthenticated entry resolves to the
 * in-app /login picker instead of immediately firing signInWithRedirect.
 *
 * Call this on an explicit user logout: after Cognito's /logout endpoint
 * redirects back to /login, we don't want to silently bounce the user straight
 * back into the IdP (which, if the upstream SSO cookie survives, would
 * re-authenticate them). The guard makes them land on the picker and choose to
 * sign in again. sessionStorage survives the cross-origin round-trip to the
 * Hosted UI and back within the same tab.
 */
export const suppressCognitoAutoLogin = (): void => {
	try {
		sessionStorage.setItem(AUTO_ATTEMPT_KEY, "true");
	} catch {
		// non-fatal
	}
};

/** Mark that the user explicitly logged out so /login shows the signed-out message. */
export const markCognitoSignedOut = (): void => {
	try {
		sessionStorage.setItem(SIGNED_OUT_KEY, "true");
	} catch {
		// non-fatal
	}
};

/** Consume the signed-out flag (read + remove). Returns true once after logout. */
export const consumeSignedOutFlag = (): boolean => {
	try {
		if (sessionStorage.getItem(SIGNED_OUT_KEY) === "true") {
			sessionStorage.removeItem(SIGNED_OUT_KEY);
			return true;
		}
	} catch {
		// non-fatal
	}
	return false;
};

/** Navigate to the in-app /login picker (honors REACT_APP_BASE_PATH). */
export const goToLoginPicker = (): void => {
	if (typeof window === "undefined" || isOnLoginRoute()) return;
	window.location.href = buildLoginPath();
};

/** Navigate to the app root (post-login landing), honoring REACT_APP_BASE_PATH. */
const enterApp = (): void => {
	const base = (environment.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
	window.location.href = `${base}/`;
};

const stashPendingProvider = (provider: string): void => {
	try {
		sessionStorage.setItem(PENDING_PROVIDER_KEY, provider);
	} catch {
		// sessionStorage unavailable (private mode) — resume just won't fire.
	}
};

/**
 * Start an interactive Cognito login for `provider` from the in-app picker.
 *
 * In Amplify v6, signInWithRedirect throws UserAlreadyAuthenticatedException
 * (and does NOT navigate) when Amplify still holds a user in local storage —
 * stale/expired tokens or a surviving Hosted-UI session. Swallowing that error
 * is what makes the login buttons appear dead. We recover by intent:
 *   - valid session  -> the user is already logged in; enter the app.
 *   - stale session  -> tear it down via a clean Hosted-UI sign-out, stashing
 *                       the chosen provider so resumePendingCognitoLogin() fires
 *                       it automatically when Cognito returns us to /login.
 */
export const startInteractiveCognitoLogin = async (provider: string): Promise<void> => {
	// Manual pick — clear the one-shot auto-login guard so this explicit attempt
	// isn't suppressed by the auto-redirect loop guard.
	resetCognitoAutoLoginGuard();
	try {
		await signInWithRedirect({ provider: { custom: provider } });
	} catch (error) {
		if ((error as { name?: string })?.name === "UserAlreadyAuthenticatedException") {
			const token = await getCognitoToken();
			if (token) {
				// Session is actually valid — they're logged in; just enter the app.
				enterApp();
				return;
			}
			// Truly stale: sign out (Hosted-UI logout round-trip back to /login)
			// and resume the chosen provider on return.
			stashPendingProvider(provider);
			await clearCognitoToken();
			return;
		}
		console.error("Cognito signInWithRedirect failed:", { provider, error });
	}
};

/**
 * On /login entry, resume a login that was interrupted by a stale-session
 * sign-out (see startInteractiveCognitoLogin). Returns true when a redirect to
 * the Hosted UI was initiated (caller should stop rendering the picker).
 */
export const resumePendingCognitoLogin = async (): Promise<boolean> => {
	let provider: string | null = null;
	try {
		provider = sessionStorage.getItem(PENDING_PROVIDER_KEY);
	} catch {
		provider = null;
	}
	if (!provider) return false;

	try {
		sessionStorage.removeItem(PENDING_PROVIDER_KEY);
	} catch {
		// non-fatal
	}

	resetCognitoAutoLoginGuard();
	try {
		await signInWithRedirect({ provider: { custom: provider } });
		return true;
	} catch (error) {
		// Local state should be clean after sign-out; if this still fails, drop
		// back to the picker rather than loop.
		console.error("Cognito resume login failed:", { provider, error });
		return false;
	}
};
